import {
  cloneElement,
  Component,
  ErrorInfo,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";

interface ErrorBoundaryProps {
  fallback: ReactElement<{ reset: () => void }>; // Expecting the fallback to have a `reset` prop
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (isValidElement(this.props.fallback)) {
        return cloneElement(this.props.fallback, {
          reset: this.resetError,
        });
      } else {
        return null;
      }
    }
    return this.props.children;
  }
}
